/**
 * GalleryCoverFlow — the Galleries beat on the marketing page.
 *
 * GALLERY-ONE killed the 24 designs, so the old rail of 24 cover stills
 * was selling something that no longer exists. The product changed shape:
 * there is ONE gallery now and the studio colours it, so the section has
 * to show one gallery many times over rather than many galleries once.
 *
 * The arrangement is the iPod/iTunes cover-flow Rennan asked for — a row
 * in perspective, the middle sleeve square on, its neighbours turned away,
 * each sitting on its own reflection. The CONTENT is ours: every sleeve is
 * a real Orbit colourway, its ink derived by the same `groundFromHex` the
 * gallery itself runs, so what the page promises is what the product does.
 *
 * Deliberately not live gallery renders. Nine `GalleryDesignThumb`s would
 * be nine full template mounts on a marketing page, and at sleeve size a
 * real gallery is mostly thumbnails of thumbnails. A sleeve reads.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { groundFromHex, fontsHref } from '@/components/gallery/templates/themes';
import { FONT_STYLES } from '@/components/gallery/templates/types';
import { useGoogleFonts } from '@/components/gallery/templates/useGoogleFonts';
import { SAMPLE_GALLERY } from '@/data/marketingSample';
import { GalleryDesignThumb } from '@/components/gallery/design/GalleryDesignThumb';
import type { GalleryCustomization } from '@/components/gallery/templates/types';

/** One sleeve: a ground, a face, a frame, a name. */
interface SleeveDef {
  id: string;
  hex: string;
  display: string;
  /** The text face, so a preview reads the way the tile does. */
  body: string;
  name: string;
  /** Whose gallery it is. Nine studios, not one studio nine times. */
  studio: string;
  /** Uppercase, wide-tracked names want the condensed faces. */
  caps?: boolean;
}

// Colour first. The old rail was 24 greys and creams because each design
// brought its own restrained palette; the point of the new gallery is that
// the studio picks, so the section has to actually show colour.
const SLEEVES: SleeveDef[] = [
  { id: 'ember',  hex: '#E8622C', display: 'Bebas Neue',         body: 'Space Grotesk',    name: 'Sun & Sea',       studio: 'Marlowe & Co.',   caps: true },
  { id: 'plum',   hex: '#1D151F', display: 'Cormorant Garamond', body: 'Figtree',          name: 'The Quiet Hours', studio: 'Juniper Reed' },
  { id: 'blush',  hex: '#F7EEEA', display: 'Bodoni Moda',        body: 'Hanken Grotesk',   name: 'Marisol',         studio: 'Casa Luz' },
  { id: 'forest', hex: '#16211C', display: 'Fraunces',           body: 'Nunito Sans',      name: 'Long Grass',      studio: 'Fieldnote' },
  { id: 'signal', hex: '#3B7DD8', display: 'Syne',               body: 'Outfit',           name: 'Blue Hour',       studio: 'Wavesync',        caps: true },
  { id: 'brass',  hex: '#E1B24C', display: 'DM Serif Display',   body: 'Space Grotesk',    name: 'Golden',          studio: 'Hale & Harper' },
  { id: 'ink',    hex: '#0A0A0A', display: 'Instrument Serif',   body: 'Plus Jakarta Sans', name: 'After the Party', studio: 'Northlight' },
  { id: 'tide',   hex: '#5FA3A5', display: 'Archivo',            body: 'Familjen Grotesk', name: 'Saltwater',       studio: 'Tern Studio',     caps: true },
  { id: 'sand',   hex: '#F0E9DF', display: 'Manrope',            body: 'Inter',            name: 'The Long Table',  studio: 'Olive & Ash' },
];

/** Where each tile starts in the sample album, so no two tiles open on
 *  the same frame. */
const SLEEVE_OFFSETS: Record<string, number> = {
  ember: 0, plum: 3, blush: 6, forest: 9, signal: 12,
  brass: 15, ink: 2, tide: 7, sand: 11,
};

/** How long one sleeve takes to cross to the next place in the row. */
const MS_PER_SLEEVE = 2400;

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

export function GalleryCoverFlow({
  /**
   * Marketing lets you open a tile for real. The new-gallery step
   * mounts the same row as a showcase — there is nothing to choose
   * there, so its tiles take no clicks and no focus.
   */
  interactive = true,
}: { interactive?: boolean } = {}) {
  const [preview, setPreview] = useState<SleeveDef | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  /** The tiles and their depth scrims, written to directly each frame. */
  const tiles = useRef<Array<{ el: HTMLButtonElement | null; scrim: HTMLDivElement | null }>>(
    SLEEVES.map(() => ({ el: null, scrim: null })),
  );
  /** Where the row is, in sleeves. Survives a preview opening and closing. */
  const pos = useRef(Math.floor(SLEEVES.length / 2));

  // Every display face the sleeves use, at the weights each really has —
  // FONT_STYLES, not a guess, because one weight a family lacks fails the
  // whole stylesheet and takes the others down with it.
  const href = useMemo(
    () =>
      fontsHref(
        SLEEVES.flatMap((s) => [
          {
            family: s.display,
            fallback: 'serif' as const,
            weights: FONT_STYLES[s.display]?.weights ?? [400],
            italic: false,
          },
          {
            family: s.body,
            fallback: 'sans-serif' as const,
            weights: (FONT_STYLES[s.body]?.weights ?? [400]).filter((w) => w <= 600),
            italic: false,
          },
        ]),
      ),
    [],
  );
  useGoogleFonts(href);

  /**
   * CONSTANT, not stepped. Rennan: "the galleries section needs to be a
   * constant carousel of the galleries."
   *
   * It used to move an INTEGER centre on a 2.2s timer and let a 900ms
   * CSS transition carry the tiles — so two thirds of every cycle was
   * the row sitting still. The position is a float now, advanced every
   * frame, and every transform is derived from it, which is also what
   * makes the rotation fluent: a tile is mid-turn at every moment
   * rather than snapping between two poses.
   *
   * WRITTEN STRAIGHT TO THE DOM, not through state. Sixty renders a
   * second of nine tiles, each with a nested sleeve and a mirrored copy
   * of it, is exactly the shape of "stuttering a lot and then just
   * skips a lot of frames" — the note that came back about the sign-in
   * animation. React mounts these once; the loop moves them. For the
   * same reason there is no CSS transition on a tile any more: the
   * frame loop IS the animation, and a transition would be fighting
   * the next frame's value.
   */
  useLayoutEffect(() => {
    const place = (p: number) => {
      for (let i = 0; i < SLEEVES.length; i++) {
        const t = tiles.current[i];
        if (!t?.el) continue;
        // Shortest way round, so the row wraps instead of unwinding.
        let offset = i - p;
        if (offset > SLEEVES.length / 2) offset -= SLEEVES.length;
        if (offset < -SLEEVES.length / 2) offset += SLEEVES.length;
        const far = Math.abs(offset);
        // Every one of these used to be a branch on `isCentre` or on an
        // integer distance, which is what made the row read as poses
        // rather than motion. They are curves through the same numbers
        // now: each lands on exactly the old value at a whole offset.
        const near = Math.min(far, 1);
        const lean = clamp(offset, -1, 1);
        const fade = far <= 1 ? 1 : far <= 2 ? 1 - (far - 1) * 0.5 : Math.max(0, 0.5 - (far - 2) * 0.5);
        t.el.style.opacity = String(fade);
        // Hidden rather than merely transparent: a tile at the back of
        // the row still costs a composite otherwise.
        t.el.style.visibility = fade <= 0.01 ? 'hidden' : 'visible';
        t.el.style.zIndex = String(Math.round(20 - far));
        t.el.style.transform =
          'translate(-50%, -50%)' +
          ' translateX(' + (offset * 86 + lean * 104) + 'px)' +
          ' translateZ(' + (120 - near * 270 - Math.max(0, far - 1) * 150) + 'px)' +
          ' rotateY(' + (-lean * 50) + 'deg)' +
          ' scale(' + (1 - near * 0.1) + ')';
        if (t.scrim) t.scrim.style.opacity = String(near * 0.12 + far * 0.1);
      }
    };

    // Placed before the first paint either way, so the row is never
    // seen stacked at the centre.
    place(pos.current);

    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced || preview) return;

    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      // Clamped so a backgrounded tab returning doesn't jump the row a
      // hundred sleeves forward on its first frame.
      const dt = Math.min(now - last, 100);
      last = now;
      pos.current = (pos.current + dt / MS_PER_SLEEVE) % SLEEVES.length;
      place(pos.current);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [preview]);

  return (
    <div
      ref={rootRef}
      className="relative select-none"
      role="group"
      aria-label="Orbit galleries, one design in many colours"
    >
      <div
        className="relative mx-auto flex items-center justify-center"
        style={{ perspective: '1800px', height: 'clamp(340px, 44vw, 560px)' }}
      >
        {/* Every sleeve is mounted, every frame. The loop above decides
            which are worth seeing — culling here would mean React
            mounting and unmounting tiles sixty times a second. */}
        {SLEEVES.map((s, i) => {
          const ground = groundFromHex(s.hex);

          return (
            <button
              key={s.id}
              ref={(el) => { tiles.current[i].el = el; }}
              type="button"
              onClick={interactive ? () => setPreview(s) : undefined}
              aria-label={interactive ? s.name + ' — open a preview' : s.name}
              tabIndex={interactive ? 0 : -1}
              aria-hidden={!interactive}
              className="absolute left-1/2 top-1/2 origin-center rounded-[6px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-4 focus-visible:ring-offset-transparent"
              style={{
                width: 'clamp(210px, 27vw, 360px)',
                cursor: interactive ? 'pointer' : 'default',
                willChange: 'transform, opacity',
                transformStyle: 'preserve-3d',
                // transform / opacity / zIndex / visibility are the
                // frame loop's; it places every tile before first paint.
                visibility: 'hidden',
              }}
            >
              <Sleeve sleeve={s} ground={ground} />
              {/* The depth dim, as a scrim rather than a filter on the
                  element. Animating filter repaints the whole subtree
                  every frame; an overlay's opacity is composited. */}
              <div
                ref={(el) => { tiles.current[i].scrim = el; }}
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-[8px] bg-black"
                style={{ opacity: 0 }}
              />

              {/* The reflection. Masked rather than faded with an overlay
                  so it works on any page background, light or dark. */}
              <div
                aria-hidden
                className="pointer-events-none absolute left-0 top-full w-full origin-top"
                style={{
                  transform: 'scaleY(-1)',
                  opacity: 0.28,
                  maskImage: 'linear-gradient(to top, transparent 0%, transparent 42%, #000 100%)',
                  WebkitMaskImage: 'linear-gradient(to top, transparent 0%, transparent 42%, #000 100%)',
                }}
              >
                <Sleeve sleeve={s} ground={ground} />
              </div>
            </button>
          );
        })}
      </div>

      {/* No dots, no caption. Rennan: "just moving through the
          sequence" — the tiles are the content, and a row of chrome
          under them was competing with the thing it labelled. */}

      {preview && <GalleryPreview sleeve={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

/**
 * The tile, opened for real.
 *
 * The tiles are hand-drawn miniatures — nine live engines on a marketing
 * page is not a trade worth making. One is. So a click mounts the actual
 * Basic template in that colourway, through the same GalleryDesignThumb
 * the app's own customize preview uses, which means what opens here
 * cannot drift from what a studio would get.
 */
function GalleryPreview({ sleeve, onClose }: { sleeve: SleeveDef; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    // The page behind must not scroll under an overlay this size.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const customization: GalleryCustomization = useMemo(
    () => ({
      backgroundHex: sleeve.hex,
      fonts: { display: sleeve.display, body: sleeve.body },
    }),
    [sleeve],
  );

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm sm:p-8"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={sleeve.name + ' — a preview'}
    >
      <div
        className="relative h-full w-full max-w-6xl overflow-hidden rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <GalleryDesignThumb
          template="basic"
          name={sleeve.name}
          studioName={sleeve.studio}
          customization={customization}
          photos={SAMPLE_GALLERY}
          downloadEnabled={false}
          frameWidth={1280}
          className="absolute inset-0 h-full w-full"
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close the preview"
          className="absolute right-4 top-4 z-[3] flex h-9 w-9 items-center justify-center rounded-full border border-white/25 bg-black/50 text-white backdrop-blur-sm transition-colors hover:bg-black/70"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/**
 * A tile is a whole gallery, not an album sleeve.
 *
 * Rennan: "they should be fully built galleries on the tiles themselves
 * showing the colors." So each tile is the real thing in miniature — the
 * masthead in that colourway's display face, then the photo wall in two
 * columns at the frames' own heights, on the ground colour. The same
 * shape the client sees at /gallery/<id>, small enough to read nine of
 * at once.
 *
 * Still not a live template mount. Nine `GalleryDesignThumb`s would be
 * nine engines on a marketing page; this is a dozen img tags.
 */
function Sleeve({
  sleeve,
  ground,
}: {
  sleeve: SleeveDef;
  ground: ReturnType<typeof groundFromHex>;
}) {
  // A different handful per tile, so the row doesn't read as one gallery
  // recoloured nine times.
  const frames = useMemo(() => {
    const start = SLEEVE_OFFSETS[sleeve.id] ?? 0;
    return Array.from({ length: 8 }, (_, k) => SAMPLE_GALLERY[(start + k) % SAMPLE_GALLERY.length]);
  }, [sleeve.id]);

  return (
    <div
      className="overflow-hidden rounded-[8px] text-left shadow-[0_26px_70px_-14px_rgba(0,0,0,0.7)]"
      style={{ background: sleeve.hex, aspectRatio: '3 / 4' }}
    >
      {/* The masthead, as the gallery draws it. */}
      <div
        className="flex items-baseline justify-between gap-2 px-[7%] pb-[3.5%] pt-[6%]"
        style={{ borderBottom: '1px solid ' + ground.line }}
      >
        <p
          className="truncate leading-none"
          style={{
            color: ground.ink,
            fontFamily: "'" + sleeve.display + "', Georgia, serif",
            fontSize: 'clamp(11px, 1.55vw, 20px)',
            letterSpacing: sleeve.caps ? '0.06em' : '-0.015em',
            textTransform: sleeve.caps ? 'uppercase' : 'none',
          }}
        >
          {sleeve.name}
        </p>
        <p
          className="shrink-0 text-[7px] font-semibold uppercase tracking-[0.18em] sm:text-[8px]"
          style={{ color: ground.muted }}
        >
          {sleeve.studio}
        </p>
      </div>

      {/* The wall. Two columns, each frame at its own height — the same
          uncropped masonry the real gallery runs, which is the whole
          claim the section is making. */}
      <div
        style={{
          columnCount: 2,
          columnGap: '4%',
          padding: '4% 7% 7%',
        }}
      >
        {frames.map((p) => (
          <img
            key={p.id}
            src={p.url}
            alt=""
            loading="lazy"
            decoding="async"
            style={{
              display: 'block',
              width: '100%',
              height: 'auto',
              marginBottom: '7%',
              borderRadius: 2,
              breakInside: 'avoid',
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default GalleryCoverFlow;
